import { PointerDragBehavior } from '@babylonjs/core/Behaviors/Meshes/pointerDragBehavior';
import { PointerInfo } from '@babylonjs/core/Events/pointerEvents';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { Color3, Matrix, Vector3 } from '@babylonjs/core/Maths/math';
import { AbstractMesh } from '@babylonjs/core/Meshes/abstractMesh';
import { CylinderBuilder } from '@babylonjs/core/Meshes/Builders/cylinderBuilder';
import { LinesMesh } from '@babylonjs/core/Meshes/linesMesh';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { Observable, Observer } from '@babylonjs/core/Misc/observable';
import { UtilityLayerRenderer } from '@babylonjs/core/Rendering/utilityLayerRenderer';
import { Scene } from '@babylonjs/core/scene';
import { Nullable } from '@babylonjs/core/types';
import { KbGizmo } from './gizmo';

/**
 * Single axis drag gizmo
 */
export class KbAxisDragGizmo extends KbGizmo {
    /**
     * Drag behavior responsible for the gizmos dragging interactions
     */
    public dragBehavior: PointerDragBehavior;
    private _pointerObserver: Nullable<Observer<PointerInfo>> = null;
    /**
     * Drag distance in babylon units that the gizmo will snap to when dragged (Default: 0)
     */
    public snapDistance = 0;
    /**
     * Event that fires each time the gizmo snaps to a new location.
     * * snapDistance is the the change in distance
     */
    public onSnapObservable = new Observable<{ snapDistance: number }>();
    public updateObservable = new Observable<{ delta: Vector3 }>();

    /** @hidden */
    public static _CreateArrow(scene: Scene, material: StandardMaterial): TransformNode {
        const arrow = new TransformNode('arrow', scene);
        const cylinder = CylinderBuilder.CreateCylinder(
            'cylinder',
            { diameterTop: 0, height: 0.075, diameterBottom: 0.0375, tessellation: 12 },
            scene
        );
        const line = CylinderBuilder.CreateCylinder(
            'cylinder',
            { diameterTop: 0.01, height: 0.275, diameterBottom: 0.01, tessellation: 6 },
            scene
        );
        const drag = CylinderBuilder.CreateCylinder(
            'cylinder',
            { diameterTop: 0.06, height: 0.4, diameterBottom: 0.06, tessellation: 6 },
            scene
        );
        line.material = material;
        cylinder.parent = arrow;
        line.parent = arrow;
        drag.parent = arrow;

        // Position arrow pointing in its drag axis
        cylinder.material = material;
        cylinder.rotation.x = Math.PI / 2;
        cylinder.position.z += 0.3;
        line.position.z += 0.275 / 2;
        line.rotation.x = Math.PI / 2;

        drag.position.z += 0.4 / 2;
        drag.rotation = line.rotation;
        drag.visibility = 0;

        return arrow;
    }

    /** @hidden */
    public static _CreateArrowInstance(scene: Scene, arrow: TransformNode): TransformNode {
        const instance = new TransformNode('arrow', scene);
        for (const mesh of arrow.getChildMeshes()) {
            const childInstance = (mesh as Mesh).createInstance(mesh.name);
            childInstance.parent = instance;
        }
        return instance;
    }

    /**
     * Creates an AxisDragGizmo
     * @param gizmoLayer The utility layer the gizmo will be added to
     * @param dragAxis The axis which the gizmo will be able to drag on
     * @param color The color of the gizmo
     */
    constructor(
        dragAxis: Vector3,
        color: Color3 = Color3.Gray(),
        gizmoLayer: UtilityLayerRenderer = UtilityLayerRenderer.DefaultUtilityLayer
    ) {
        super(gizmoLayer);

        // Create Material
        const coloredMaterial = new StandardMaterial('', gizmoLayer.utilityLayerScene);
        coloredMaterial.emissiveColor = color;
        // coloredMaterial.diffuseColor = color;
        // coloredMaterial.specularColor = color.subtract(new Color3(0.1, 0.1, 0.1));

        const hoverMaterial = new StandardMaterial('', gizmoLayer.utilityLayerScene);
        hoverMaterial.emissiveColor = color.add(new Color3(0.3, 0.3, 0.3));
        // hoverMaterial.diffuseColor = color.add(new Color3(0.3, 0.3, 0.3));

        // Build mesh on root node
        const arrow = KbAxisDragGizmo._CreateArrow(gizmoLayer.utilityLayerScene, coloredMaterial);

        arrow.lookAt(this._rootMesh.position.add(dragAxis));
        arrow.scaling.scaleInPlace(1 / 3);
        arrow.parent = this._rootMesh;

        let currentSnapDragDistance = 0;
        const tmpVector = new Vector3();
        const tmpSnapEvent = { snapDistance: 0 };
        // Add drag behavior to handle events when the gizmo is dragged
        this.dragBehavior = new PointerDragBehavior({ dragAxis: dragAxis });
        this.dragBehavior.moveAttached = false;
        this._rootMesh.addBehavior(this.dragBehavior);

        const localDelta = new Vector3();
        const tmpMatrix = new Matrix();
        this.dragBehavior.onDragObservable.add(event => {
            if (this.attachedMesh) {
                // Convert delta to local translation if it has a parent
                if (this.attachedMesh.parent) {
                    this.attachedMesh.parent.computeWorldMatrix().invertToRef(tmpMatrix);
                    tmpMatrix.setTranslationFromFloats(0, 0, 0);
                    Vector3.TransformCoordinatesToRef(event.delta, tmpMatrix, localDelta);
                } else {
                    localDelta.copyFrom(event.delta);
                }
                // Snapping logic
                if (this.snapDistance == 0) {
                    this.attachedMesh.position.addInPlace(localDelta);
                    this.updateObservable.notifyObservers({ delta: localDelta });
                } else {
                    currentSnapDragDistance += event.dragDistance;
                    if (Math.abs(currentSnapDragDistance) > this.snapDistance) {
                        const dragSteps = Math.floor(Math.abs(currentSnapDragDistance) / this.snapDistance);
                        currentSnapDragDistance = currentSnapDragDistance % this.snapDistance;
                        localDelta.normalizeToRef(tmpVector);
                        tmpVector.scaleInPlace(this.snapDistance * dragSteps);
                        this.attachedMesh.position.addInPlace(tmpVector);
                        tmpSnapEvent.snapDistance = this.snapDistance * dragSteps;
                        this.updateObservable.notifyObservers({ delta: tmpVector });
                        this.onSnapObservable.notifyObservers(tmpSnapEvent);
                    }
                }
            }
        });

        this._pointerObserver = gizmoLayer.utilityLayerScene.onPointerObservable.add(pointerInfo => {
            if (this._customMeshSet) {
                return;
            }
            const isHovered =
                pointerInfo.pickInfo &&
                this._rootMesh.getChildMeshes().indexOf(<Mesh>pointerInfo.pickInfo.pickedMesh) != -1;
            const material = isHovered ? hoverMaterial : coloredMaterial;
            this._rootMesh.getChildMeshes().forEach(m => {
                m.material = material;
                if ((<LinesMesh>m).color) {
                    (<LinesMesh>m).color = material.diffuseColor;
                }
            });
        });
    }
    protected _attachedMeshChanged(value: Nullable<AbstractMesh>) {
        if (this.dragBehavior) {
            this.dragBehavior.enabled = value ? true : false;
        }
    }
    /**
     * Disposes of the gizmo
     */
    public dispose() {
        this.onSnapObservable.clear();
        this.gizmoLayer.utilityLayerScene.onPointerObservable.remove(this._pointerObserver);
        this.dragBehavior.detach();
        super.dispose();
    }
}
